import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Luxistt';
const DEFAULT_TITLE = 'Luxistt — Short stays that feel like home';
const DEFAULT_DESCRIPTION =
  'Affordable furnished short stays in Nigeria. Browse Luxistt apartments in Abuja, Enugu, Lagos and beyond — transparent pricing, online booking, and stays that feel like home.';
const DEFAULT_IMAGE = '/og-image.jpg';
const SITE_URL = 'https://luxistt.com';

/**
 * SEO head component. Drop into any page to set title, description, OG, Twitter cards, and JSON-LD.
 *
 * @param {object} props
 * @param {string} [props.title]
 * @param {string} [props.description]
 * @param {string} [props.path] - URL path e.g. "/rooms"
 * @param {string} [props.image] - OG image URL
 * @param {string} [props.type] - OG type (default "website")
 * @param {boolean} [props.noindex] - add noindex if true
 * @param {object} [props.jsonLd] - JSON-LD structured data object
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  image,
  type = 'website',
  noindex = false,
  jsonLd,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const canonical = `${SITE_URL}${path}`;
  const ogImage = image || `${SITE_URL}${DEFAULT_IMAGE}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_NG" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
